// ==========================================================================
// Magic Link Verification Page
// Handles URLs like /auth/TOKEN_HERE — verifies the token and logs in
// ==========================================================================

import { backgroundImage, weatherIcon } from '@/assets';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { verifyMagicLink } from '@/services/user.service';
import { applyFSPBranding } from '@/stores/authStore';
import { identifyById } from '@/lib/mixpanel';

export default function MagicLinkVerifyPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (!token || verifiedRef.current) return;
    verifiedRef.current = true;

    async function verify() {
      try {
        const response = await verifyMagicLink(token!);

        // Clear ALL cached queries from any previous session to prevent
        // stale data from a different user leaking through
        queryClient.clear();

        // Store access token
        localStorage.setItem('access_token', response.access_token);

        // Identify in Mixpanel immediately so onboarding events use the user
        // id rather than the anonymous $device:... id.
        if (response.user?.id) {
          identifyById(response.user.id);
        }

        // Apply branding
        if (response.user?.fsp) {
          applyFSPBranding(response.user.fsp);
        }

        // Navigate to /home — AuthProvider will redirect based on fresh GET /user data
        // (onboarded/verified flags from magic link response may not be accurate)
        navigate('/home', { replace: true });
      } catch {
        // Toast shown by axios interceptor
        setError(t('auth.magicLink.invalidOrExpired'));
      }
    }

    verify();
  }, [token, navigate, queryClient]);

  if (error) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        <p className="text-white text-lg font-medium mb-4">{error}</p>
        <button
          onClick={() => navigate('/auth/phone', { replace: true })}
          className="h-12 px-6 bg-brand-primary text-white text-sm font-semibold rounded-2xl"
        >
          {t('auth.magicLink.goToLogin')}
        </button>
      </div>
    );
  }

  // Loading state while verifying — same for all FSPs
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <img
        src={weatherIcon}
        alt="Loading"
        className="w-[140px] h-[140px] object-contain mb-8"
      />
      <div className="flex gap-1">
        <div className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}
